import React, { useState, useEffect } from "react";
import { useAuth } from "../lib/auth";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import ImageUpload from "../components/ImageUpload";
import {
  LayoutDashboard, FolderKanban, FileText, Mail, Settings as SettingsIcon,
  LogOut, Plus, Edit2, Trash2, Eye, BarChart3, TrendingUp, Users, Inbox, X, Loader2, ExternalLink
} from "lucide-react";

const TABS = [
  { id: "overview", label: "Vue d'ensemble", icon: LayoutDashboard },
  { id: "projects", label: "Réalisations", icon: FolderKanban },
  { id: "blog", label: "Blog", icon: FileText },
  { id: "quotes", label: "Demandes", icon: Mail },
  { id: "settings", label: "Paramètres", icon: SettingsIcon },
];

export default function AdminDashboard() {
  const { user, loading, refresh, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [refreshed, setRefreshed] = useState(false);

  useEffect(() => {
    // Force fresh fetch after callback
    if (!refreshed) { refresh(); setRefreshed(true); }
  }, [refresh, refreshed]);

  useEffect(() => {
    if (!loading && !user?.is_admin) nav("/admin/login");
  }, [loading, user, nav]);

  if (loading || !user?.is_admin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#09090b]">
        <Loader2 className="animate-spin text-[#d4af37]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090b] text-white flex flex-col md:flex-row">
      {/* Sidebar */}
      <aside className="md:w-64 md:border-r border-white/10 md:min-h-screen flex md:flex-col" data-testid="admin-sidebar">
        <div className="hidden md:block px-6 py-6 border-b border-white/10">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-[#d4af37] flex items-center justify-center font-display text-base font-bold text-black">B</div>
            <span className="font-display text-base">Console</span>
          </div>
          <div className="text-xs text-zinc-500 mt-3 truncate">{user.email}</div>
        </div>
        <nav className="flex md:flex-col gap-1 p-3 overflow-x-auto md:overflow-visible no-scrollbar">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`flex items-center gap-3 px-3 py-2.5 text-sm transition-all whitespace-nowrap ${
                tab === t.id ? "bg-[#d4af37]/10 text-[#d4af37] border-l-2 border-[#d4af37]" : "text-zinc-400 hover:text-white hover:bg-white/5"
              }`}
            >
              <t.icon size={16} /> {t.label}
            </button>
          ))}
          <button
            onClick={async () => { await logout(); nav("/admin/login"); }}
            data-testid="logout-btn"
            className="flex items-center gap-3 px-3 py-2.5 text-sm text-zinc-400 hover:text-white hover:bg-white/5 md:mt-auto"
          >
            <LogOut size={16} /> Déconnexion
          </button>
        </nav>
      </aside>

      <main className="flex-1 p-6 md:p-10" data-testid="admin-main">
        {tab === "overview" && <Overview />}
        {tab === "projects" && <ProjectsAdmin />}
        {tab === "blog" && <BlogAdmin />}
        {tab === "quotes" && <QuotesAdmin />}
        {tab === "settings" && <SettingsAdmin />}
      </main>
    </div>
  );
}

// ============== OVERVIEW ==============
function Overview() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {}); }, []);
  if (!stats) return <Loader2 className="animate-spin text-[#d4af37]" />;
  const cards = [
    { label: "Visiteurs uniques", value: stats.unique_visitors, icon: Users },
    { label: "Pages vues", value: stats.page_views, icon: Eye },
    { label: "Demandes", value: stats.quotes_total, icon: Inbox, hint: `${stats.quotes_new} nouvelles` },
    { label: "Taux de conversion", value: `${stats.conversion_rate}%`, icon: TrendingUp },
    { label: "Réalisations", value: stats.projects_total, icon: FolderKanban, hint: `${stats.projects_published} publiées` },
    { label: "Articles", value: stats.blog_total, icon: FileText },
  ];
  return (
    <div data-testid="overview-panel">
      <h1 className="font-display text-3xl mb-1">Vue d'ensemble</h1>
      <p className="text-zinc-500 text-sm mb-10">Performance globale du site Blackline Studio.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 mb-10">
        {cards.map((c) => (
          <div key={c.label} className="bg-[#0a0a0c] p-6" data-testid={`stat-card-${c.label}`}>
            <div className="flex items-start justify-between mb-4">
              <c.icon size={18} className="text-[#d4af37]" />
            </div>
            <div className="font-display text-3xl mb-1">{c.value}</div>
            <div className="text-xs tracking-wide text-zinc-500 uppercase">{c.label}</div>
            {c.hint && <div className="text-xs text-zinc-600 mt-1">{c.hint}</div>}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="border border-white/10 p-6">
          <h3 className="font-display text-lg mb-4 flex items-center gap-2"><BarChart3 size={16} className="text-[#d4af37]" /> Pages les plus vues</h3>
          {stats.top_pages.length === 0 ? <p className="text-zinc-500 text-sm">Aucune donnée pour le moment.</p> : (
            <ul className="space-y-2">
              {stats.top_pages.map((p, i) => (
                <li key={i} className="flex justify-between text-sm py-2 border-b border-white/5">
                  <span className="text-zinc-300 truncate mr-3">{p.path}</span>
                  <span className="text-[#d4af37] font-mono">{p.count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="border border-white/10 p-6">
          <h3 className="font-display text-lg mb-4 flex items-center gap-2"><TrendingUp size={16} className="text-[#d4af37]" /> Provenance du trafic</h3>
          {stats.referrers.length === 0 ? <p className="text-zinc-500 text-sm">Aucune source identifiée.</p> : (
            <ul className="space-y-2">
              {stats.referrers.map((r, i) => (
                <li key={i} className="flex justify-between text-sm py-2 border-b border-white/5">
                  <span className="text-zinc-300 truncate mr-3">{r.source}</span>
                  <span className="text-[#d4af37] font-mono">{r.count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

// ============== PROJECTS ==============
function ProjectsAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/admin/projects").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const onSave = async (form) => {
    try {
      if (editing?.id) await api.put(`/admin/projects/${editing.id}`, form);
      else await api.post("/admin/projects", form);
      toast.success(editing ? "Réalisation mise à jour" : "Réalisation créée");
      setOpen(false); setEditing(null); load();
    } catch { toast.error("Erreur lors de l'enregistrement"); }
  };
  const onDelete = async (id) => {
    if (!confirm("Supprimer cette réalisation ?")) return;
    await api.delete(`/admin/projects/${id}`);
    toast.success("Supprimée"); load();
  };

  return (
    <div data-testid="projects-panel">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-3xl mb-1">Réalisations</h1>
          <p className="text-zinc-500 text-sm">Gestion du portfolio public.</p>
        </div>
        <button onClick={() => { setEditing({}); setOpen(true); }} data-testid="add-project-btn"
          className="btn-gold px-5 py-2.5 text-sm inline-flex items-center gap-2"><Plus size={16} /> Ajouter</button>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-white/10 p-16 text-center text-zinc-500" data-testid="no-projects-admin">
          Aucune réalisation. Commencez par en ajouter une.
        </div>
      ) : (
        <div className="border border-white/10 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0a0a0c] text-xs uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="text-left p-4">Titre</th>
                <th className="text-left p-4">Catégorie</th>
                <th className="text-left p-4">Secteur</th>
                <th className="text-left p-4">Publié</th>
                <th className="text-right p-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p, idx) => (
                <tr key={p.id} className="border-t border-white/5 hover:bg-white/5" data-testid={`project-row-${idx}`}>
                  <td className="p-4 font-medium">{p.title}</td>
                  <td className="p-4 text-zinc-400">{p.category}</td>
                  <td className="p-4 text-zinc-400">{p.industry || "—"}</td>
                  <td className="p-4">
                    <span className={`px-2 py-1 text-xs ${p.published ? "bg-emerald-950/40 text-emerald-300" : "bg-zinc-900 text-zinc-500"}`}>
                      {p.published ? "Oui" : "Non"}
                    </span>
                  </td>
                  <td className="p-4 text-right">
                    <div className="inline-flex gap-2">
                      {p.demo_url && <a href={p.demo_url} target="_blank" rel="noreferrer" className="p-2 hover:bg-white/5"><ExternalLink size={14} /></a>}
                      <button onClick={() => { setEditing(p); setOpen(true); }} data-testid={`edit-project-${idx}`} className="p-2 hover:bg-white/5"><Edit2 size={14} /></button>
                      <button onClick={() => onDelete(p.id)} data-testid={`delete-project-${idx}`} className="p-2 hover:bg-red-950/30 text-red-400"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open && (
        <ProjectModal
          initial={editing}
          onClose={() => { setOpen(false); setEditing(null); }}
          onSave={onSave}
        />
      )}
    </div>
  );
}

function ProjectModal({ initial, onClose, onSave }) {
  const [f, setF] = useState({
    title: initial?.title || "", category: initial?.category || "site-vitrine", industry: initial?.industry || "",
    description: initial?.description || "", features: (initial?.features || []).join("\n"),
    image_url: initial?.image_url || "", demo_url: initial?.demo_url || "",
    client_name: initial?.client_name || "", seo_title: initial?.seo_title || "",
    seo_description: initial?.seo_description || "", published: initial?.published !== false,
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target?.type === "checkbox" ? e.target.checked : (e.target?.value ?? e) });
  const submit = (e) => {
    e.preventDefault();
    if (!f.title || !f.description) return toast.error("Titre et description requis");
    onSave({ ...f, features: f.features.split("\n").map(s => s.trim()).filter(Boolean) });
  };
  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="project-modal-admin">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-[#0a0a0c] border border-white/10 max-w-2xl w-full my-8 p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-2xl">{initial?.id ? "Modifier" : "Nouvelle"} réalisation</h2>
          <button type="button" onClick={onClose} className="p-2"><X size={18} /></button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Titre *"><input required value={f.title} onChange={set("title")} className={inputCls} data-testid="pm-title" /></Field>
          <Field label="Catégorie">
            <select value={f.category} onChange={set("category")} className={inputCls} data-testid="pm-category">
              <option value="site-vitrine">Site vitrine</option>
              <option value="rdv">Prise de RDV</option>
              <option value="click-collect">Click & Collect</option>
              <option value="refonte">Refonte</option>
              <option value="identite">Identité visuelle</option>
              <option value="hybride">C&C + RDV</option>
            </select>
          </Field>
          <Field label="Secteur"><input value={f.industry} onChange={set("industry")} className={inputCls} data-testid="pm-industry" /></Field>
          <Field label="Client"><input value={f.client_name} onChange={set("client_name")} className={inputCls} data-testid="pm-client" /></Field>
          <Field label="Image URL" full>
            <ImageUpload
              value={f.image_url}
              onChange={(url) => setF({ ...f, image_url: url || "" })}
              testIdPrefix="pm-image-upload"
            />
          </Field>
          <Field label="URL démo" full><input value={f.demo_url} onChange={set("demo_url")} className={inputCls} data-testid="pm-demo" placeholder="https://..." /></Field>
          <Field label="Description *" full><textarea required rows={4} value={f.description} onChange={set("description")} className={inputCls + " resize-none"} data-testid="pm-description" /></Field>
          <Field label="Fonctionnalités (une par ligne)" full><textarea rows={4} value={f.features} onChange={set("features")} className={inputCls + " resize-none"} data-testid="pm-features" placeholder="Réservation 24/7&#10;Paiement en ligne" /></Field>
          <Field label="SEO Title"><input value={f.seo_title} onChange={set("seo_title")} className={inputCls} data-testid="pm-seo-title" /></Field>
          <Field label="SEO Description"><input value={f.seo_description} onChange={set("seo_description")} className={inputCls} data-testid="pm-seo-desc" /></Field>
        </div>
        <label className="flex items-center gap-2 mt-5 text-sm cursor-pointer">
          <input type="checkbox" checked={f.published} onChange={set("published")} data-testid="pm-published" /> Publié
        </label>
        <div className="flex justify-end gap-3 mt-8">
          <button type="button" onClick={onClose} className="px-5 py-2.5 text-sm text-zinc-400 hover:text-white">Annuler</button>
          <button type="submit" className="btn-gold px-6 py-2.5 text-sm" data-testid="pm-save">Enregistrer</button>
        </div>
      </form>
    </div>
  );
}

// ============== BLOG ==============
function BlogAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/admin/blog").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const onSave = async (form) => {
    try {
      if (editing?.id) await api.put(`/admin/blog/${editing.id}`, form);
      else await api.post("/admin/blog", form);
      toast.success("Enregistré"); setOpen(false); setEditing(null); load();
    } catch { toast.error("Erreur"); }
  };
  const onDelete = async (id) => {
    if (!confirm("Supprimer cet article ?")) return;
    await api.delete(`/admin/blog/${id}`); toast.success("Supprimé"); load();
  };

  return (
    <div data-testid="blog-panel">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-3xl mb-1">Journal</h1>
          <p className="text-zinc-500 text-sm">Articles et actualités.</p>
        </div>
        <button onClick={() => { setEditing({}); setOpen(true); }} data-testid="add-post-btn"
          className="btn-gold px-5 py-2.5 text-sm inline-flex items-center gap-2"><Plus size={16} /> Nouvel article</button>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-white/10 p-16 text-center text-zinc-500" data-testid="no-posts-admin">Aucun article.</div>
      ) : (
        <div className="border border-white/10 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0a0a0c] text-xs uppercase tracking-wider text-zinc-500">
              <tr><th className="text-left p-4">Titre</th><th className="text-left p-4">Date</th><th className="text-left p-4">Publié</th><th className="text-right p-4">Actions</th></tr>
            </thead>
            <tbody>
              {items.map((p, idx) => (
                <tr key={p.id} className="border-t border-white/5 hover:bg-white/5" data-testid={`post-row-${idx}`}>
                  <td className="p-4 font-medium">{p.title}</td>
                  <td className="p-4 text-zinc-400">{new Date(p.created_at).toLocaleDateString("fr-FR")}</td>
                  <td className="p-4">
                    <span className={`px-2 py-1 text-xs ${p.published ? "bg-emerald-950/40 text-emerald-300" : "bg-zinc-900 text-zinc-500"}`}>
                      {p.published ? "Oui" : "Non"}
                    </span>
                  </td>
                  <td className="p-4 text-right">
                    <button onClick={() => { setEditing(p); setOpen(true); }} data-testid={`edit-post-${idx}`} className="p-2 hover:bg-white/5"><Edit2 size={14} /></button>
                    <button onClick={() => onDelete(p.id)} data-testid={`delete-post-${idx}`} className="p-2 hover:bg-red-950/30 text-red-400"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open && <BlogModal initial={editing} onClose={() => { setOpen(false); setEditing(null); }} onSave={onSave} />}
    </div>
  );
}

function BlogModal({ initial, onClose, onSave }) {
  const [f, setF] = useState({
    title: initial?.title || "", excerpt: initial?.excerpt || "", content: initial?.content || "",
    cover_image: initial?.cover_image || "", tags: (initial?.tags || []).join(", "),
    seo_title: initial?.seo_title || "", seo_description: initial?.seo_description || "",
    published: initial?.published !== false,
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target?.type === "checkbox" ? e.target.checked : (e.target?.value ?? e) });
  const submit = (e) => {
    e.preventDefault();
    if (!f.title || !f.excerpt || !f.content) return toast.error("Titre, extrait et contenu requis");
    onSave({ ...f, tags: f.tags.split(",").map(s => s.trim()).filter(Boolean) });
  };
  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="blog-modal-admin">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-[#0a0a0c] border border-white/10 max-w-2xl w-full my-8 p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-2xl">{initial?.id ? "Modifier" : "Nouvel"} article</h2>
          <button type="button" onClick={onClose} className="p-2"><X size={18} /></button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Titre *" full><input required value={f.title} onChange={set("title")} className={inputCls} data-testid="bm-title" /></Field>
          <Field label="Image de couverture" full>
            <ImageUpload
              value={f.cover_image}
              onChange={(url) => setF({ ...f, cover_image: url || "" })}
              testIdPrefix="bm-cover-upload"
            />
          </Field>
          <Field label="Extrait *" full><textarea required rows={2} value={f.excerpt} onChange={set("excerpt")} className={inputCls + " resize-none"} data-testid="bm-excerpt" /></Field>
          <Field label="Contenu *" full><textarea required rows={10} value={f.content} onChange={set("content")} className={inputCls + " resize-none"} data-testid="bm-content" /></Field>
          <Field label="Tags (séparés par virgule)" full><input value={f.tags} onChange={set("tags")} className={inputCls} data-testid="bm-tags" /></Field>
          <Field label="SEO Title"><input value={f.seo_title} onChange={set("seo_title")} className={inputCls} data-testid="bm-seo-title" /></Field>
          <Field label="SEO Description"><input value={f.seo_description} onChange={set("seo_description")} className={inputCls} data-testid="bm-seo-desc" /></Field>
        </div>
        <label className="flex items-center gap-2 mt-5 text-sm cursor-pointer">
          <input type="checkbox" checked={f.published} onChange={set("published")} data-testid="bm-published" /> Publié
        </label>
        <div className="flex justify-end gap-3 mt-8">
          <button type="button" onClick={onClose} className="px-5 py-2.5 text-sm text-zinc-400 hover:text-white">Annuler</button>
          <button type="submit" className="btn-gold px-6 py-2.5 text-sm" data-testid="bm-save">Enregistrer</button>
        </div>
      </form>
    </div>
  );
}

// ============== QUOTES ==============
function QuotesAdmin() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const load = () => api.get("/admin/quotes").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    await api.put(`/admin/quotes/${id}`, { status });
    toast.success("Statut mis à jour"); load();
    setSelected(null);
  };

  return (
    <div data-testid="quotes-panel">
      <h1 className="font-display text-3xl mb-1">Demandes</h1>
      <p className="text-zinc-500 text-sm mb-8">Formulaires reçus depuis le site.</p>

      {items.length === 0 ? (
        <div className="border border-dashed border-white/10 p-16 text-center text-zinc-500" data-testid="no-quotes">
          Aucune demande pour le moment.
        </div>
      ) : (
        <div className="border border-white/10 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0a0a0c] text-xs uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="text-left p-4">Nom</th><th className="text-left p-4">Email</th>
                <th className="text-left p-4">Service</th><th className="text-left p-4">Date</th>
                <th className="text-left p-4">Statut</th><th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((q, idx) => (
                <tr key={q.id} className="border-t border-white/5 hover:bg-white/5 cursor-pointer" onClick={() => setSelected(q)} data-testid={`quote-row-${idx}`}>
                  <td className="p-4 font-medium">{q.name}</td>
                  <td className="p-4 text-zinc-400">{q.email}</td>
                  <td className="p-4 text-zinc-400 truncate max-w-[200px]">{q.service}</td>
                  <td className="p-4 text-zinc-500 text-xs">{new Date(q.created_at).toLocaleDateString("fr-FR")}</td>
                  <td className="p-4"><StatusBadge status={q.status} /></td>
                  <td className="p-4 text-right text-[#d4af37] text-xs">Voir →</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur flex items-start justify-center p-4 overflow-y-auto" onClick={() => setSelected(null)} data-testid="quote-detail-modal">
          <div className="bg-[#0a0a0c] border border-white/10 max-w-xl w-full my-8 p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="font-display text-2xl">{selected.name}</h2>
                <p className="text-zinc-500 text-sm mt-1">{new Date(selected.created_at).toLocaleString("fr-FR")}</p>
              </div>
              <button onClick={() => setSelected(null)} className="p-2"><X size={18} /></button>
            </div>
            <div className="space-y-4 text-sm">
              <Row label="Email">{selected.email}</Row>
              <Row label="Téléphone">{selected.phone || "—"}</Row>
              <Row label="Entreprise">{selected.company || "—"}</Row>
              <Row label="Service">{selected.service}</Row>
              <Row label="Secteur">{selected.industry || "—"}</Row>
              <Row label="Budget">{selected.budget || "—"}</Row>
              <div>
                <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Message</div>
                <p className="text-zinc-300 leading-relaxed whitespace-pre-wrap">{selected.message}</p>
              </div>
            </div>
            <div className="mt-6 pt-6 border-t border-white/10">
              <div className="text-xs uppercase tracking-wider text-zinc-500 mb-3">Statut</div>
              <div className="flex flex-wrap gap-2">
                {["new", "contacted", "won", "lost"].map((s) => (
                  <button key={s} onClick={() => updateStatus(selected.id, s)} data-testid={`status-${s}`}
                    className={`px-3 py-1.5 text-xs border transition-all ${selected.status === s ? "bg-[#d4af37] text-black border-[#d4af37]" : "border-white/10 text-zinc-400 hover:border-white/30"}`}>
                    {{ new: "Nouvelle", contacted: "Contacté", won: "Gagnée", lost: "Perdue" }[s]}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    new: { l: "Nouvelle", c: "bg-blue-950/40 text-blue-300" },
    contacted: { l: "Contacté", c: "bg-amber-950/40 text-amber-300" },
    won: { l: "Gagnée", c: "bg-emerald-950/40 text-emerald-300" },
    lost: { l: "Perdue", c: "bg-zinc-900 text-zinc-500" },
  };
  const m = map[status] || map.new;
  return <span className={`px-2 py-1 text-xs ${m.c}`}>{m.l}</span>;
}

// ============== SETTINGS ==============
function SettingsAdmin() {
  const [f, setF] = useState(null);
  useEffect(() => { api.get("/settings").then(({ data }) => setF(data)); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put("/admin/settings", f);
      toast.success("Paramètres enregistrés");
    } catch { toast.error("Erreur"); }
  };
  if (!f) return <Loader2 className="animate-spin text-[#d4af37]" />;
  return (
    <form onSubmit={save} data-testid="settings-panel">
      <h1 className="font-display text-3xl mb-1">Paramètres</h1>
      <p className="text-zinc-500 text-sm mb-8">SEO global, contact et réseaux sociaux.</p>

      <section className="mb-10">
        <h2 className="font-display text-lg mb-5 text-[#d4af37]">SEO global</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Titre du site" full><input value={f.site_title || ""} onChange={set("site_title")} className={inputCls} data-testid="set-title" /></Field>
          <Field label="Description SEO" full><textarea rows={2} value={f.site_description || ""} onChange={set("site_description")} className={inputCls + " resize-none"} data-testid="set-desc" /></Field>
          <Field label="Mots-clés" full><input value={f.keywords || ""} onChange={set("keywords")} className={inputCls} data-testid="set-keywords" /></Field>
          <Field label="Google Analytics ID (G-XXXX)"><input value={f.ga_measurement_id || ""} onChange={set("ga_measurement_id")} className={inputCls} data-testid="set-ga" placeholder="G-XXXXXXX" /></Field>
        </div>
      </section>

      <section className="mb-10">
        <h2 className="font-display text-lg mb-5 text-[#d4af37]">Contact</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Email"><input value={f.contact_email || ""} onChange={set("contact_email")} className={inputCls} data-testid="set-email" /></Field>
          <Field label="Numéro WhatsApp public (format international, ex: +33612345678)"><input value={f.whatsapp_number || ""} onChange={set("whatsapp_number")} className={inputCls} data-testid="set-whatsapp" placeholder="+33612345678" /></Field>
          <Field label="Téléphone privé (interne admin, non affiché)"><input value={f.contact_phone || ""} onChange={set("contact_phone")} className={inputCls} data-testid="set-phone" /></Field>
          <Field label="Adresse"><input value={f.contact_address || ""} onChange={set("contact_address")} className={inputCls} data-testid="set-address" /></Field>
        </div>
      </section>

      <section className="mb-10">
        <h2 className="font-display text-lg mb-5 text-[#d4af37]">Réseaux sociaux</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Instagram URL"><input value={f.social_instagram || ""} onChange={set("social_instagram")} className={inputCls} data-testid="set-ig" /></Field>
          <Field label="LinkedIn URL"><input value={f.social_linkedin || ""} onChange={set("social_linkedin")} className={inputCls} data-testid="set-li" /></Field>
        </div>
      </section>

      <button type="submit" className="btn-gold px-6 py-3 text-sm" data-testid="save-settings">Enregistrer les paramètres</button>
    </form>
  );
}

// ============== UI helpers ==============
const inputCls = "w-full bg-[#0a0a0c] border border-white/10 px-3 py-2.5 text-white placeholder:text-zinc-600 focus:border-[#d4af37] outline-none text-sm transition-colors";

function Field({ label, full, children }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <span className="text-xs text-zinc-400 mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex justify-between gap-4 border-b border-white/5 pb-3">
      <span className="text-zinc-500 text-xs uppercase tracking-wider">{label}</span>
      <span className="text-zinc-200 text-right">{children}</span>
    </div>
  );
}
